/**
 * Test script for High Contrast Mode Support
 * This script can be run in the browser console to test high contrast functionality
 */

function testHighContrastSupport() {
  console.log('🎨 Testing High Contrast Mode Support...\n');
  
  // Test 1: Check if HighContrastSupport class is available
  console.log('1. Testing HighContrastSupport availability:');
  if (window.highContrastSupport) {
    console.log('✅ HighContrastSupport is available');
    
    // Get current status
    const status = window.highContrastSupport.getStatus();
    console.log('📊 Current Status:', status);
  } else {
    console.log('❌ HighContrastSupport is not available');
    return;
  }
  
  // Test 2: Check CSS Custom Properties support
  console.log('\n2. Testing CSS Custom Properties:');
  const testElement = document.createElement('div');
  testElement.style.setProperty('--test-var', 'red');
  testElement.style.color = 'var(--test-var)';
  document.body.appendChild(testElement);
  
  const computedStyle = window.getComputedStyle(testElement);
  const colorValue = computedStyle.color;
  
  if (colorValue === 'red' || colorValue === 'rgb(255, 0, 0)') {
    console.log('✅ CSS Custom Properties are supported');
  } else {
    console.log('❌ CSS Custom Properties are not supported');
  }
  
  document.body.removeChild(testElement);
  
  // Test 3: Check media query support
  console.log('\n3. Testing Media Query Support:');
  
  if (window.matchMedia) {
    console.log('✅ matchMedia is supported');
    
    // Test high contrast media query
    try {
      const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
      console.log('📱 High Contrast Query:', highContrastQuery.matches ? 'Active' : 'Inactive');
    } catch (e) {
      console.log('❌ High contrast media query not supported:', e.message);
    }
    
    // Test forced colors media query
    try {
      const forcedColorsQuery = window.matchMedia('(forced-colors: active)');
      console.log('🎨 Forced Colors Query:', forcedColorsQuery.matches ? 'Active' : 'Inactive');
    } catch (e) {
      console.log('❌ Forced colors media query not supported:', e.message);
    }
    
    // Test Windows high contrast media query
    try {
      const windowsHCQuery = window.matchMedia('(-ms-high-contrast: active)');
      console.log('🪟 Windows High Contrast Query:', windowsHCQuery.matches ? 'Active' : 'Inactive');
    } catch (e) {
      console.log('❌ Windows high contrast media query not supported:', e.message);
    }
  } else {
    console.log('❌ matchMedia is not supported');
  }
  
  // Test 4: Check for high contrast classes on document
  console.log('\n4. Testing Document Classes:');
  const html = document.documentElement;
  const classes = html.classList;
  
  console.log('📄 Document classes:', Array.from(classes).join(', ') || 'None');
  
  if (classes.contains('high-contrast')) {
    console.log('✅ High contrast class is active');
  } else {
    console.log('ℹ️ High contrast class is not active');
  }
  
  if (classes.contains('no-css-custom-properties')) {
    console.log('⚠️ CSS custom properties fallback is active');
  }
  
  if (classes.contains('no-backdrop-filter')) {
    console.log('⚠️ Backdrop filter fallback is active');
  }
  
  if (classes.contains('no-gradients')) {
    console.log('⚠️ Gradients fallback is active');
  }
  
  // Test 5: Test manual high contrast toggle
  console.log('\n5. Testing Manual High Contrast Toggle:');
  console.log('🔄 Toggling high contrast mode...');
  
  const wasHighContrast = classes.contains('high-contrast');
  window.highContrastSupport.toggleHighContrast();
  
  setTimeout(() => {
    const isHighContrast = html.classList.contains('high-contrast');
    
    if (wasHighContrast !== isHighContrast) {
      console.log('✅ High contrast toggle works');
      console.log(`📊 State changed from ${wasHighContrast} to ${isHighContrast}`);
      
      // Toggle back to original state
      window.highContrastSupport.toggleHighContrast();
      console.log('🔄 Restored original state');
    } else {
      console.log('❌ High contrast toggle did not work');
    }
  }, 100);
  
  // Test 6: Test element visibility
  console.log('\n6. Testing Element Visibility:');
  
  setTimeout(() => {
    const visibilityResults = window.highContrastSupport.testHighContrastVisibility();
    
    if (visibilityResults && visibilityResults.length > 0) {
      console.log('✅ Visibility test completed');
      console.log(`📊 Tested ${visibilityResults.length} elements`);
      
      const visibleCount = visibilityResults.filter(r => r.visible).length;
      const notVisibleCount = visibilityResults.length - visibleCount;
      
      console.log(`✅ Visible elements: ${visibleCount}`);
      if (notVisibleCount > 0) {
        console.log(`⚠️ Not visible elements: ${notVisibleCount}`);
        
        // Show first few non-visible elements
        const notVisible = visibilityResults.filter(r => !r.visible).slice(0, 3);
        notVisible.forEach(element => {
          console.log(`   - ${element.element} (${element.type})`);
        });
      }
    } else {
      console.log('❌ Visibility test failed or no elements found');
    }
  }, 200);
  
  // Test 7: Test color contrast
  console.log('\n7. Testing Color Contrast:');
  
  // Create test elements with different color combinations
  const testColors = [
    { bg: '#000000', fg: '#ffffff', name: 'Black/White' },
    { bg: '#ffffff', fg: '#000000', name: 'White/Black' },
    { bg: '#2563EB', fg: '#ffffff', name: 'Primary Blue/White' },
    { bg: '#0F172A', fg: '#F8FAFC', name: 'Dark/Light' }
  ];
  
  testColors.forEach(color => {
    const contrast = calculateContrastRatio(color.bg, color.fg);
    const wcagAA = contrast >= 4.5;
    const wcagAAA = contrast >= 7;
    
    console.log(`🎨 ${color.name}: ${contrast.toFixed(2)}:1 ${wcagAA ? '✅ AA' : '❌ AA'} ${wcagAAA ? '✅ AAA' : '❌ AAA'}`);
  });
  
  console.log('\n🎉 High Contrast Support Test Complete!');
  console.log('\n💡 To manually test:');
  console.log('   - Visit /high-contrast-test page');
  console.log('   - Enable system high contrast mode');
  console.log('   - Use browser dev tools to simulate high contrast');
  console.log('   - Test with screen readers');
}

/**
 * Calculate contrast ratio between two colors
 */
function calculateContrastRatio(color1, color2) {
  const luminance1 = getLuminance(color1);
  const luminance2 = getLuminance(color2);
  
  const lighter = Math.max(luminance1, luminance2);
  const darker = Math.min(luminance1, luminance2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Get relative luminance of a color
 */
function getLuminance(color) {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  
  // Apply gamma correction
  const rs = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gs = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bs = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  
  // Calculate luminance
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

// Make test function available globally
window.testHighContrastSupport = testHighContrastSupport;

// Auto-run test if in development mode
if (process.env.NODE_ENV === 'development') {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(testHighContrastSupport, 1000);
    });
  } else {
    setTimeout(testHighContrastSupport, 1000);
  }
}

export { testHighContrastSupport, calculateContrastRatio, getLuminance };